import {
  Avatar,
  Box,
  Collapse,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useRef } from "react";
import { ChatRoleBadge } from "Components/Chat/ChatRoleBadge";
import { useSelectedUserIneligibility } from "Hooks/Chat/useSelectedUserIneligibility";
import { CHAT_PICKER_SECTION_SPACING } from "Components/Chat/chatConstants";
import type { SocialUserSearchDto } from "Infrastructure/Api/Api";
import { isAccessRoleCode } from "Models/AccessRoleCodes";
import { Resources, type TFunction, useResource } from "Translations/Resources";
import { getUserDisplayName } from "Utils/Chat/getUserDisplayName";
import { getContextProfilePhotoUrl } from "Utils/Image/getProfilePhotoUrl";

const RESOURCES = Resources.Chat.NewChat;

const AVATAR_SIZE = 56;
/** Tap target for the remove button. Sized explicitly rather than left to icon + padding. */
const REMOVE_BUTTON_SIZE = 24;
/**
 * How far the remove button sits outside the avatar's box. In PIXELS, and the row's top padding is
 * derived from it — this theme sets `spacing: 4`, so a spacing-unit padding is half what MUI's
 * default base would give and used to clip the button's top edge. Small, because a button this
 * size is better sitting ON the avatar than floating off its corner.
 */
const REMOVE_BUTTON_OVERHANG = 2;
/** Wide enough for the avatar plus a name that can wrap to two short lines. */
const ENTRY_WIDTH = 72;

export type SelectedUser = {
  identityUserPublicID: string;
  name: string;
  /**
   * Resolved avatar URL, so a picked person keeps the picture they had where they were picked.
   * A URL rather than a file name because the sources differ: search results derive one from the
   * DTO, while a counterpart taken from a Stream channel already carries a resolved one.
   */
  avatarUrl?: string;
  accessRoleCode?: string | null;
  /**
   * Whether the person may join a verified-only group. Undefined when the source did not know
   * (a counterpart taken from a Stream channel) — treated as eligible here; the server decides.
   */
  isFullMoone?: boolean;
  /**
   * The person's handle — null when they have not joined Chat (no handle, Social ADR-0004), so
   * they cannot be brought into a new conversation. Undefined when the source did not know (a
   * counterpart taken from a Stream channel) — treated as eligible here; the server decides.
   */
  handle?: string | null;
};

type Props = {
  selectedUsers: ReadonlyArray<SelectedUser>;
  /**
   * The conversation admits Full Moone personas only (App ADR-0012). Picked people who are not
   * stay in the row, greyed, with a note on why — they were picked before the rule was set, or
   * came along from a 1:1, and the owner has to see whom to remove.
   */
  isFullMooneRequired?: boolean;
  onRemoveUser: (identityUserPublicID: string) => void;
};

/**
 * A search result as a picked person: the name and avatar are resolved here, where they were
 * picked, so the chip keeps them whatever the source later shows.
 */
export const toSelectedUser = (
  user: SocialUserSearchDto,
  t: TFunction,
): SelectedUser => {
  const {
    identityUserPublicID,
    profilePicture,
    accessRoleCode,
    isFullMoone,
    handle,
  } = user;

  return {
    identityUserPublicID,
    name: getUserDisplayName(user, t),
    avatarUrl: getContextProfilePhotoUrl(identityUserPublicID, profilePicture),
    accessRoleCode,
    isFullMoone,
    handle,
  };
};

/**
 * Toggle, not add: the search row and the chip's remove button both come through here, so the
 * two views of a selection cannot drift apart.
 */
export const toggleSelectedUser = (
  selected: ReadonlyArray<SelectedUser>,
  user: SocialUserSearchDto,
  t: TFunction,
): Array<SelectedUser> => {
  const { identityUserPublicID } = user;

  return selected.some((u) => u.identityUserPublicID === identityUserPublicID)
    ? selected.filter((u) => u.identityUserPublicID !== identityUserPublicID)
    : [...selected, toSelectedUser(user, t)];
};

/**
 * The people picked so far, sitting between the search input and the results so the set you are
 * building stays in view while you browse. Scrolls horizontally rather than wrapping: a growing
 * group would otherwise push the results off the screen, which matters most on the mobile bottom
 * sheet where there is least room.
 *
 * Collapses in and out instead of appearing: picking the first person would otherwise shove the
 * list below down by the row's full height, and this sits directly above that list.
 */
export const ChatSelectedMembers = ({
  selectedUsers,
  isFullMooneRequired,
  onRemoveUser,
}: Props) => {
  const { t } = useResource();
  const getIneligibility = useSelectedUserIneligibility(isFullMooneRequired);

  // The list is already empty by the time the collapse starts, so without holding the last
  // non-empty one the row would blink out and only THEN close. Derived purely from props and
  // idempotent, which is what makes writing a ref during render safe here.
  const lastNonEmptyRef = useRef(selectedUsers);

  if (selectedUsers.length > 0) {
    lastNonEmptyRef.current = selectedUsers;
  }

  const usersToRender =
    selectedUsers.length > 0 ? selectedUsers : lastNonEmptyRef.current;
  // Each reason gets its own note — the fix is the same for all of them, remove the person.
  const ineligibilities = new Set(usersToRender.map(getIneligibility));

  return (
    <Collapse in={selectedUsers.length > 0}>
      {/* The spacing above the row lives INSIDE the collapse, so it shrinks away with the content
          rather than being a gap that survives to zero height and then vanishes in one step. */}
      <Stack gap={1} pt={CHAT_PICKER_SECTION_SPACING}>
        <Typography variant="subtitle2">
          {/* Counts what is RENDERED, not what is selected — during the closing animation the
              selection is already empty while the avatars are still on screen. */}
          {t(RESOURCES.MembersLabel)} ({usersToRender.length})
        </Typography>
        <Stack
          direction="row"
          gap={2}
          sx={{
            overflowX: "auto",
            // Scrolls, but without a scrollbar taking a stripe out of the row: at this height a
            // horizontal bar is most of the visual weight. The avatars clipping at the edge is
            // what signals there is more to see. Chromium and Safari need the pseudo-element,
            // Firefox the standard property.
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
            // Clears the remove button's overhang, plus its 1px border and rounding.
            pt: `${REMOVE_BUTTON_OVERHANG + 2}px`,
            pb: 1,
          }}
        >
          {usersToRender.map((user) => {
            const { identityUserPublicID, name, avatarUrl, accessRoleCode } =
              user;
            const isIneligible = getIneligibility(user) !== undefined;
            // Greyed, not removed — and only the avatar and name: the X stays fully visible,
            // because removing them is the fix.
            const dimmedSx = isIneligible ? { opacity: 0.4 } : undefined;

            return (
              <Stack
                key={identityUserPublicID}
                alignItems="center"
                gap={0.5}
                flexShrink={0}
                width={ENTRY_WIDTH}
              >
                <Box position="relative" display="inline-flex">
                  <Avatar
                    src={avatarUrl}
                    alt={name}
                    sx={{
                      width: AVATAR_SIZE,
                      height: AVATAR_SIZE,
                      ...dimmedSx,
                    }}
                  >
                    {name.charAt(0).toUpperCase()}
                  </Avatar>
                  <ChatRoleBadge
                    accessRole={
                      isAccessRoleCode(accessRoleCode)
                        ? accessRoleCode
                        : undefined
                    }
                    avatarSize={AVATAR_SIZE}
                  />
                  <IconButton
                    size="small"
                    onClick={() => onRemoveUser(identityUserPublicID)}
                    aria-label={t(RESOURCES.RemoveMember, { name })}
                    sx={{
                      position: "absolute",
                      top: -REMOVE_BUTTON_OVERHANG,
                      left: -REMOVE_BUTTON_OVERHANG,
                      width: REMOVE_BUTTON_SIZE,
                      height: REMOVE_BUTTON_SIZE,
                      p: 0,
                      bgcolor: "background.paper",
                      border: "1px solid",
                      borderColor: "divider",
                      "&:hover": { bgcolor: "background.paper" },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
                <Typography
                  variant="caption"
                  textAlign="center"
                  sx={{
                    // Two lines then ellipsis: names vary wildly in length and a ragged row of
                    // different heights reads worse than a clipped one.
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    wordBreak: "break-word",
                    ...dimmedSx,
                  }}
                >
                  {name}
                </Typography>
              </Stack>
            );
          })}
        </Stack>
        {ineligibilities.has("notFullMoone") && (
          <Typography variant="caption" color="warning.dark">
            {t(RESOURCES.IneligibleSelectedWarning)}
          </Typography>
        )}
        {ineligibilities.has("customerWritesFirst") && (
          <Typography variant="caption" color="warning.dark">
            {t(RESOURCES.CustomerSelectedWarning)}
          </Typography>
        )}
        {ineligibilities.has("noHandle") && (
          <Typography variant="caption" color="warning.dark">
            {t(RESOURCES.NoHandleSelectedWarning)}
          </Typography>
        )}
      </Stack>
    </Collapse>
  );
};
